import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Shield,
  Briefcase,
  User,
  Bell,
  Activity,
  Lock,
  Edit2,
  UserX,
  ChevronDown,
  Check,
  X,
  MessageSquare,
  Slack,
  CheckCircle,
  XCircle,
  UserCheck,
} from "react-feather";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getUserAPI,
  getUsersAPI,
  updateUserAPI,
  deleteUserAPI,
  getRolesAPI,
  getDepartmentsAPI,
} from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import StatusBadge from "../../../Components/shared/StatusBadge";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./UserDetail.module.scss";

const PRIMARY = "#644998";

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [{ key: "profile", label: "Profile", icon: User }];

// ─── Reusable detail row ──────────────────────────────────────────────────────
function DetailRow({ icon: Icon, label, value, editable, children }) {
  return (
    <div className={styles.detailRow}>
      <div className={styles.detailIcon}>
        <Icon size={14} />
      </div>
      <div className={styles.detailContent}>
        <span className={styles.detailLabel}>{label}</span>
        {editable ? (
          children
        ) : (
          <span className={styles.detailValue}>{value || "—"}</span>
        )}
      </div>
    </div>
  );
}

// ─── Custom Select Dropdown ───────────────────────────────────────────────────
function SelectDropdown({
  options,
  value,
  onChange,
  placeholder = "Select...",
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={styles.selectWrap} ref={ref}>
      <button
        type="button"
        className={`${styles.selectTrigger} ${open ? styles.open : ""}`}
        onClick={() => setOpen(!open)}
      >
        <span>{selected?.label || placeholder}</span>
        <ChevronDown
          size={13}
          className={`${styles.chevron} ${open ? styles.open : ""}`}
        />
      </button>
      {open && (
        <div className={styles.selectDropdown}>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`${styles.selectOption} ${opt.value === value ? styles.selected : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className={styles.selectCheck}>
                {opt.value === value && <Check size={11} />}
              </span>
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label }) {
  return (
    <label className={styles.toggle}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={styles.toggleTrack}>
        <span className={styles.toggleThumb} />
      </span>
      <span className={styles.toggleLabel}>{label}</span>
    </label>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("profile");

  // Modals
  const [roleOpen, setRoleOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newRole, setNewRole] = useState("");

  // Profile form
  const [profile, setProfile] = useState({
    phone: "",
    preferredLanguage: "en",
    jobTitle: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, list, r, d] = await Promise.all([
        getUserAPI(id),
        getUsersAPI(),
        getRolesAPI(),
        getDepartmentsAPI(),
      ]);
      const data = u.data;
      setUser(data);
      setAllUsers(list.data || []);
      setRoles(r.data || []);
      setDepts(d.data || []);
      // Set default role to user's first role
      setNewRole(data.roles?.[0] || "");
      setProfile({
        phone: data.phone || "",
        preferredLanguage: data.preferences?.language || "en",
        jobTitle: data.jobTitle || "",
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const initials = useMemo(() => {
    if (!user?.name) return "?";
    return user.name
      .split(" ")
      .map((s) => s[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [user]);

  // Check if this is the last admin
  const isLastAdmin = useMemo(() => {
    const admins = allUsers.filter(
      (u) => u.roles?.includes("company_admin") && u.status === "active",
    );
    return user?.roles?.includes("company_admin") && admins.length === 1;
  }, [allUsers, user]);

  const saveProfile = async () => {
    try {
      await updateUserAPI(id, {
        phone: profile.phone,
        jobTitle: profile.jobTitle,
        preferences: { language: profile.preferredLanguage },
      });
      toast.success("Profile saved");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const applyRole = async () => {
    try {
      await updateUserAPI(id, { roles: [newRole] });
      toast.success("Role updated");
      setRoleOpen(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Toggle user status between active and disabled
  const toggleUserStatus = async () => {
    const newStatus = user.status === "active" ? "disabled" : "active";

    try {
      await updateUserAPI(id, { status: newStatus });
      toast.success(
        `User ${newStatus === "active" ? "activated" : "deactivated"} successfully`,
      );
      setStatusOpen(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ── Loading / Error states ──
  if (loading && !user) {
    return (
      <div>
        <PageHeader
          title="User"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Users", path: companyUrl("users") },
            { label: "…" },
          ]}
        />
        <LoadingSkeleton rows={6} cols={2} />
      </div>
    );
  }

  if (error && !user) {
    return (
      <div>
        <PageHeader
          title="User"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Users", path: companyUrl("users") },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load user"
          description={error.message}
          actionLabel="Back to Users"
          onAction={() => navigate(companyUrl("users"))}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={user?.name}
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Users", path: companyUrl("users") },
          { label: user?.name },
        ]}
        secondaryActions={[
          {
            label: "Back to Users",
            onClick: () => navigate(companyUrl("users")),
          },
        ]}
      />

      {/* ── Hero card ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.avatarLg}>{initials}</div>
          <div className={styles.heroInfo}>
            <h2 className={styles.heroName}>{user?.name}</h2>
            <div className={styles.heroMeta}>
              <StatusBadge status={user?.status} />
              {user?.roles?.map((r, i) => (
                <span key={i} className={styles.roleChip}>
                  {r}
                </span>
              ))}
              {user?.departments?.map((d, i) => (
                <span key={i} className={styles.deptChip}>
                  {d.departmentName || d}
                </span>
              ))}
            </div>
            <div className={styles.heroContact}>
              <span>
                <Mail size={12} /> {user?.email}
              </span>
              {user?.phone && (
                <span>
                  <Phone size={12} /> {user?.phone}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className={styles.heroActions}>
          <button
            className={styles.btnOutline}
            onClick={() => setRoleOpen(true)}
          >
            <Shield size={13} /> Change Role
          </button>

          {/* Show Deactivate button if status is active, Show Activate button if status is not active */}
          {user?.status === "active" ? (
            <button
              className={`${styles.btnOutline} ${styles.btnDanger}`}
              disabled={isLastAdmin}
              onClick={() => setStatusOpen(true)}
            >
              <UserX size={13} /> Deactivate
            </button>
          ) : (
            <button
              className={`${styles.btnOutline} ${styles.btnSuccess}`}
              onClick={() => setStatusOpen(true)}
            >
              <UserCheck size={13} /> Activate
            </button>
          )}
        </div>
      </div>

      {isLastAdmin && user?.status === "active" && (
        <div className={styles.warnBanner}>
          ⚠️ Cannot deactivate the last active Company Admin. Please assign
          another admin first.
        </div>
      )}

      {/* Profile Tab */}
      {tab === "profile" && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>Profile Information</div>
          <div className={styles.cardBody}>
            <div className={styles.profileGrid}>
              {/* Read-only fields */}
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Full Name</label>
                <input
                  className={styles.input}
                  readOnly
                  value={user?.name || ""}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Email</label>
                <input
                  className={styles.input}
                  readOnly
                  value={user?.email || ""}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>User ID</label>
                <input
                  className={styles.input}
                  readOnly
                  value={user?.userId || ""}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Departments</label>
                <input
                  className={styles.input}
                  readOnly
                  value={
                    user?.departments
                      ?.map((d) => d.departmentName || d)
                      .join(", ") || "—"
                  }
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Preferred Language</label>
                <SelectDropdown
                  options={[
                    { value: "en", label: "English" },
                    { value: "ar", label: "Arabic" },
                  ]}
                  value={profile.preferredLanguage}
                  onChange={(v) =>
                    setProfile((s) => ({ ...s, preferredLanguage: v }))
                  }
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Last Login</label>
                <input
                  className={styles.input}
                  readOnly
                  value={
                    user?.auth?.lastLoginAt
                      ? new Date(user.auth.lastLoginAt).toLocaleString()
                      : "Never"
                  }
                />
              </div>
            </div>

            <div className={styles.cardFooter}>
              <button className={styles.btnPrimary} onClick={saveProfile}>
                <Edit2 size={13} /> Save Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Role Modal ── */}
      <Modal show={roleOpen} onHide={() => setRoleOpen(false)} centered>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>Change Role</h5>
            <button
              className={styles.modalClose}
              onClick={() => setRoleOpen(false)}
            >
              <X size={15} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <p className={styles.modalNote}>
              Updating role for <strong>{user?.name}</strong>. This will take
              effect immediately.
            </p>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>New Role</label>
              <SelectDropdown
                options={roles.map((r) => ({
                  value: r.roleKey,
                  label: r.name,
                }))}
                value={newRole}
                onChange={setNewRole}
              />
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button
              className={styles.btnOutline}
              onClick={() => setRoleOpen(false)}
            >
              Cancel
            </button>
            <button className={styles.btnPrimary} onClick={applyRole}>
              Apply Role
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Status Toggle Confirm Modal (Activate/Deactivate) ── */}
      <Modal show={statusOpen} onHide={() => setStatusOpen(false)} centered>
        <div className={styles.modal}>
          <div
            className={`${styles.modalHeader} ${user?.status === "active" ? styles.modalHeaderDanger : styles.modalHeaderSuccess}`}
          >
            <h5 className={styles.modalTitle}>
              {user?.status === "active"
                ? "Deactivate User?"
                : "Activate User?"}
            </h5>
            <button
              className={styles.modalClose}
              onClick={() => setStatusOpen(false)}
            >
              <X size={15} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <p className={styles.modalNote}>
              {user?.status === "active" ? (
                <>
                  <strong>{user?.name}</strong> will immediately lose access to
                  the platform. This action can be reversed by activating the
                  user again.
                </>
              ) : (
                <>
                  <strong>{user?.name}</strong> will regain full access to the
                  platform. The user will be able to log in and use all features
                  based on their role.
                </>
              )}
            </p>
          </div>
          <div className={styles.modalFooter}>
            <button
              className={styles.btnOutline}
              onClick={() => setStatusOpen(false)}
            >
              Cancel
            </button>
            <button
              className={`${styles.btnPrimary} ${user?.status === "active" ? styles.btnDanger : styles.btnSuccess}`}
              onClick={toggleUserStatus}
            >
              {user?.status === "active" ? (
                <>
                  <UserX size={13} /> Deactivate
                </>
              ) : (
                <>
                  <UserCheck size={13} /> Activate
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
